import {
  Box,
  Table as ChakraTable,
  TableCellProps,
  TableColumnHeaderProps,
  TableRowProps,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  ExpandedState,
  Row,
  SortingState,
  TableOptions,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { merge } from 'lodash';
import React, { UIEvent, useCallback, useState } from 'react';
import { TableVirtuoso } from 'react-virtuoso';

import { ShadowCell } from './ShadowCell';
import {
  ColumnHeader,
  TableBodyStyle,
  TableHeader,
  TableHeaderText,
  TableStyle,
  TableTdStyle,
  TableThStyle,
  TableTrStyle,
  TableWrapStyle,
} from './Table.styles';
import { TableRow } from './TableRow';

interface TableProps<D extends object>
  extends Omit<TableOptions<D>, 'getCoreRowModel'> {
  tdProps?: TableCellProps;
  tableHeaderProps?: TableColumnHeaderProps;
  genericSort?: (field: string) => void;
  customHeaderIds?: Array<string>; // array of column ids that should have hoverable headers for custom filter and sort effects
  onEndReached?: (() => void) | undefined;
  columnHeaderIdToHeapNameMap?: Record<string, string>;
  onRowClick?: (row: Row<D>) => void;
  tableRowStyle?: TableRowProps;
  tableRowTrackId?: string;
  getSelectedRowStyles?: (row: D) => TableCellProps;
}

export const Table = <T extends {}>({
  data,
  columns,
  getSelectedRowStyles,
  tdProps,
  tableHeaderProps,
  customHeaderIds,
  onEndReached = undefined,
  columnHeaderIdToHeapNameMap,
  onRowClick,
  tableRowStyle,
  tableRowTrackId,
  ...props
}: TableProps<T>) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      expanded,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    ...props,
  });
  const rows = table.getRowModel().rows;

  const toggleRowExpansion = useCallback(
    (row: Row<T>) => () => {
      const currentRowExpanded = !!row.getIsExpanded();
      table.toggleAllRowsExpanded(false);
      row.toggleExpanded(!currentRowExpanded);
    },
    [table],
  );

  const onTableRowClick = useCallback(
    (row: Row<T>) => () => {
      toggleRowExpansion(row);
      onRowClick && onRowClick(row);
    },
    [onRowClick, toggleRowExpansion],
  );

  const onScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if ((e.target as HTMLInputElement).scrollLeft > 0) {
        // This is scrolled horizontally
        setIsScrolled(true);
        return;
      }
      setIsScrolled(false);
    },
    [setIsScrolled],
  );

  const tableTdStyleProps = useCallback(
    (zindex: number) => {
      // zindex is used to make sure the top sticky cell makes higher stack context than the bottom sticky cell
      return merge(TableTdStyle(zindex), tdProps);
    },
    [tdProps],
  );

  return (
    <>
      <Box
        position="relative"
        borderRadius="12px"
        overflow="hidden"
        zIndex={0}
        {...TableWrapStyle}
      >
        <TableVirtuoso
          endReached={() => {
            onEndReached && onEndReached();
          }}
          useWindowScroll
          overscan={300}
          totalCount={rows.length}
          onScroll={onScroll}
          style={{
            width: '100%',
            overflowX: 'auto',
          }}
          components={{
            Table: ({ style, ...props }) => {
              return (
                <ChakraTable
                  variant="unstyled"
                  style={{ ...style }}
                  {...TableStyle}
                  {...props}
                />
              );
            },
            TableHead: React.forwardRef(({ style: _, ...props }, ref) => (
              <Thead {...TableHeader} {...props} ref={ref} />
            )),
            TableBody: React.forwardRef(({ style: _, ...props }, ref) => (
              <Tbody {...TableBodyStyle} {...props} ref={ref} />
            )),
            TableRow: (props) => {
              const rowIndex = props['data-index'];
              const row = rows[rowIndex];

              return (
                <TableRow
                  row={row}
                  as={'tr'}
                  key={row.id}
                  onClick={onTableRowClick(row)}
                  track-id={tableRowTrackId}
                  {...props}
                  {...TableTrStyle}
                  {...tableRowStyle}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <Td
                      padding={0}
                      key={cell.id}
                      {...tableTdStyleProps(rows.length - rowIndex + 1)}
                      {...getSelectedRowStyles?.(row.original)}
                    >
                      {index === 0 ? (
                        <ShadowCell
                          needsShadow={isScrolled && index === 0}
                          comp={cell.column.columnDef.cell}
                          props={cell.getContext()}
                        />
                      ) : (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )
                      )}
                    </Td>
                  ))}
                </TableRow>
              );
            },
          }}
          fixedHeaderContent={() => {
            return table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id} {...ColumnHeader}>
                {headerGroup.headers.map((header, index) => {
                  return (
                    <Th
                      key={header.id}
                      {...TableThStyle}
                      colSpan={header.colSpan}
                      track-id={columnHeaderIdToHeapNameMap?.[header.id]}
                      {...{
                        ...TableHeaderText({
                          onHoverEffect: header.column.getCanSort(),
                          noPadding: customHeaderIds?.includes(header.id),
                        }),
                        ...(header.column.getCanSort() && {
                          onClick: () => props.genericSort?.(header.column.id),
                        }),
                      }}
                      {...tableHeaderProps}
                    >
                      {!header.isPlaceholder && (
                        <ShadowCell
                          needsShadow={isScrolled && index === 0}
                          comp={header.column.columnDef.header}
                          props={header.getContext()}
                        />
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ));
          }}
        />
      </Box>
    </>
  );
};
